/** @jsxImportSource ./simboluri-jsx */
// Panoul de sub articolul de săpătură: unde pleacă pământul, și cât costă.
//
// Se deschide din deviz, pe articolul care are `evacuare_m3` — adică singurul
// care chiar trimite ceva la groapă. Trei lucruri, în ordinea în care omul le
// întreabă: *care groapă se folosește*, *de ce aceea*, *ce se schimbă dacă aleg
// alta*.
//
// DE CE „REGENEREAZĂ" E UN BUTON, nu un efect al clicului pe hartă. Un clic pe
// hartă spune «vreau să văd cât ar fi cu asta»; recalcularea devizului întreg
// durează și schimbă cifre pe care omul poate le compară cu altceva în acel
// moment. Separate, poate umbla pe hartă fără să-i sară raportul sub ochi, și
// apasă când s-a hotărât.

import { useCallback, useEffect, useState } from "react";
import { useT, useTx, useTxn } from "../i18n";
import type { Cheie } from "../i18n";
import GropiHarta, { GropiTabel } from "./GropiHarta";
import { Lamurire } from "../viewer/cost/Termeni";
import { ApiError } from "../api";
import { alegeGroapa, getGropi } from "./apiStructura";
import type { GropiRaspuns } from "./apiStructura";
import { bani } from "./bani";

// De ce nu s-a putut alege — spus în cuvinte pe care omul le poate rezolva.
const MOTIVE: Record<string, Cheie> = {
  no_site: "struct.gpan.no_site",
  no_volume: "struct.gpan.no_volume",
  no_options: "struct.gpan.no_options",
};

export default function GropiPanou({
  projectId, volumM3, peRegenerare,
}: {
  projectId: string;
  /** m³ care pleacă de pe șantier — `evacuare_m3` al articolului de săpătură */
  volumM3: number;
  /** cere recalcularea devizului; panoul nu știe cum se face */
  peRegenerare: () => Promise<void> | void;
}) {
  const [date, setDate] = useState<GropiRaspuns | null>(null);
  const t = useT();
  const tx = useTx();
  const txn = useTxn();
  const [motiv, setMotiv] = useState<string>("");
  const [eroare, setEroare] = useState("");
  const [ocupat, setOcupat] = useState(false);
  const [schimbat, setSchimbat] = useState(false);
  // Harta e ÎNCHISĂ la început. Două motive, și al doilea e cel tehnic:
  //   * devizul se citește pe rânduri; o hartă de 320 px deschisă din oficiu
  //     împinge restul articolelor afară din ecran la fiecare expandare;
  //   * montată abia la deschidere, harta găsește celula deja așezată. Montată
  //     odată cu rândul, prinde un `<td>` de lățime zero, iar încadrarea de
  //     atunci iese la zoom 0.
  const [harta, setHarta] = useState(false);

  const incarca = useCallback(() => {
    setEroare(""); setMotiv("");
    getGropi(projectId, volumM3)
      .then(setDate)
      .catch((e: unknown) => {
        // 409 nu e o defecțiune: e „nu se poate alege încă", cu un motiv pe
        // care ecranul îl traduce în ceva ce omul poate rezolva. Corpul stă în
        // `ApiError.date` — v. api.ts, unde refuzurile poartă ce trebuie arătat.
        const reason = e instanceof ApiError && e.status === 409
          ? (e.date as { reason?: string } | undefined)?.reason
          : undefined;
        if (reason)
          setMotiv(MOTIVE[reason]
            ? t(MOTIVE[reason])
            : t("struct.gpan.fara_motiv"));
        else setEroare(String((e as Error).message ?? e));
        setDate(null);
      });
  }, [projectId, volumM3]);

  useEffect(incarca, [incarca]);

  const alege = useCallback(async (code: string) => {
    setOcupat(true);
    try {
      await alegeGroapa(projectId, code);
      setSchimbat(true);
      // Se recitesc variantele, nu se modifică local: serverul e cel care
      // decide ce s-a salvat (o alegere a omului nu se rescrie de una automată),
      // iar ecranul trebuie să arate ce e în bază, nu ce am cerut noi.
      await getGropi(projectId, volumM3).then(setDate).catch(() => {});
    } catch (e) {
      setEroare(String((e as Error).message ?? e));
    } finally {
      setOcupat(false);
    }
  }, [projectId, volumM3]);

  const regenereaza = useCallback(async () => {
    setOcupat(true);
    try { await peRegenerare(); setSchimbat(false); }
    finally { setOcupat(false); }
  }, [peRegenerare]);

  if (motiv) return <div className="sg-muted gh-motiv">{motiv}</div>;
  if (eroare) return <div className="sg-eroare">{eroare}</div>;
  if (!date) return <div className="sg-muted">{t("struct.gpan.se_socotesc")}</div>;

  const ales = date.chosen;
  const celMaiIeftin = date.options.find((o) => o.in_range !== false) ?? date.options[0];
  const pierdut = ales ? Number(ales.total) - Number(celMaiIeftin.total) : 0;
  // Câte gropi au rămas pe estimare. Nota de sub tabel se scrie din cifra asta,
  // nu dintr-un text fix: pe un mediu cu router pornit ea e zero și n-are rost
  // să scrie nimeni că „distanțele sunt aproximative", iar pe unul fără router
  // e totul și n-are voie să scrie nimeni că sunt măsurate.
  const estimate = date.options.filter((o) => o.distance_source === "estimated");
  const lamurire = (
    <>
      {estimate.length === 0 ? (
        // Rutat tot. Rămâne de spus singurul lucru care e într-adevăr
        // aproximativ: nu drumul, ci capătul lui. Coordonatele gropilor sunt
        // în general ale localității, nu ale porții rampei — v. antetul din
        // `planificare/date/gropi_bucuresti.json`.
        <>{tx("struct.gpan.rutat_tot")}</>
      ) : estimate.length === date.options.length ? (
        <>{tx("struct.gpan.estimat_tot")}</>
      ) : (
        <>{txn("struct.gpan.estimat_unele#n", estimate.length)}</>
      )}
      {t("struct.gpan.tarife")}
    </>
  );

  return (
    <div className="gh-panou">
      <div className="gh-antet">
        <div>
          {ales ? (
            <>
              <strong>{ales.name}</strong>
              {date.chosen_by === "user"
                ? <span className="gh-eticheta">{t("struct.gpan.aleasa_tine")}</span>
                : <span className="gh-eticheta gh-auto">{t("struct.gpan.cea_ieftina")}</span>}
              <div className="sg-muted">
                {t("struct.gpan.sumar", {
                  m3: volumM3.toFixed(0),
                  km: ales.distance_km,
                  curse: ales.trips,
                  transport: bani(Number(ales.transport_cost)),
                  depozitare: bani(Number(ales.disposal_cost)),
                })}
                <strong>{bani(Number(ales.total))}</strong>
              </div>
            </>
          ) : (
            // NICIO GROAPĂ ÎN RAZĂ: devizul e pe prețul plafon, spus pe față —
            // catalogul nu e complet, iar omul poate alege de mai jos o groapă
            // de dincolo de rază dacă știe că ea primește pământul.
            <>
              <strong>{t("struct.gpan.plafon_titlu")}</strong>
              <span className="gh-eticheta">{t("struct.gpan.plafon_eticheta")}</span>
              <Lamurire text={t("struct.gpan.plafon", {
                km: date.cap?.distance_km ?? "—",
                tarif: date.cap?.disposal_eur_t ?? "—",
              })} />
            </>
          )}
          {ales && pierdut > 0 && (
            // Costul alegerii proprii, spus pe față. Omul are dreptul s-o facă
            // — dar nu pe nevăzute.
            <div className="sg-muted">
              {t("struct.gpan.pierdut", {
                suma: bani(pierdut), nume: celMaiIeftin.name,
              })}
            </div>
          )}
        </div>
        <button className="sg-buton" disabled={ocupat || !schimbat}
                onClick={regenereaza}
                title={t(schimbat ? "struct.gpan.regen_titlu"
                                 : "struct.gpan.regen_nimic")}>
          {t(ocupat ? "struct.gpan.se_regenereaza" : "struct.gpan.regenereaza")}
        </button>
      </div>

      <button type="button" className="gh-comutator"
              aria-expanded={harta}
              onClick={() => setHarta((v) => !v)}>
        {harta ? "▾" : "▸"} {t("struct.gpan.harta")}
        <span className="sg-muted">
          {t("struct.gpan.nr_gropi", { n: date.options.length })}
          {harta ? "" : t("struct.gpan.inchisa")}
        </span>
      </button>
      <Lamurire text={lamurire} />
      {harta && <GropiHarta date={date} peAlegere={alege} ocupat={ocupat} />}

      <GropiTabel optiuni={date.options} alesCod={ales?.code ?? null}
                  peAlegere={alege} ocupat={ocupat} />

      {schimbat && (
        <div className="gh-avertisment">
          {t("struct.gpan.schimbat")}
        </div>
      )}
      {date.site.precision === "localitate" && (
        // Cifrele de mai sus se măsoară din centrul zonei. Pe o localitate de
        // câțiva km, două gropi apropiate ca total pot să-și schimbe locul —
        // iar asta trebuie spus lângă cifre, nu doar pe hartă.
        <div className="gh-avertisment">
          {tx("struct.gpan.localitate")}
        </div>
      )}
    </div>
  );
}
